import { Collapse, Form, Space } from 'antd';
import { Layout } from 'components/Layout/Layout';
import { useInventoryMeterReadingGroups } from 'hooks/api/reading-groups/useFind';
import { useUpdateInventoryMeterReadingGroup } from 'hooks/api/reading-groups/useUpdate';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useEnmonMeters } from '../../../hooks/api/enmon-tools/meters/useFind';
import { GroupDetailFooter } from '../components/Footer';
import { GroupDetailCard } from '../components/GroupDetailCard';
import { ReadingGroupDetailPage as messages } from '../messages';
import { GroupDetailsForm } from '../types';
import { AddMeterForm } from './components/AddMeterForm';
import { GroupMetersTable } from './components/Table';

export const ReadingGroupDetailPage = () => {
  const intl = useIntl();
  const { id } = useParams();
  const parsedId = parseInt(id ?? '');

  const { data: groupResponse, isLoading: isFetchingReadingGroups } = useInventoryMeterReadingGroups(
    { filter: { id: parsedId } },
    { enabled: !!parsedId },
  );
  const group = groupResponse?.data?.[0];

  const inventoryMeterIds = useMemo(() => {
    return (
      group?.inventory_meters
        ?.map(inventoryMeter => inventoryMeter.id?.toString())
        .filter((inventoryMeterId): inventoryMeterId is string => Boolean(id)) ?? []
    );
  }, [id, group]);

  const { data, isFetching: isLoadingEnmonMeters } = useEnmonMeters(
    { toolsIds: inventoryMeterIds },
    { enabled: !!group },
  );

  const { mutate: updateMeterGroup, isLoading: isUpdatingMeterGroup } = useUpdateInventoryMeterReadingGroup();

  const isLoading = isFetchingReadingGroups || isLoadingEnmonMeters;

  const meters = useMemo(() => {
    const enmonMeters =
      data?.result.filter(meter => inventoryMeterIds.includes(meter.toolsId ? meter.toolsId : '')) ?? [];
    return enmonMeters.map(({ toolsId, name }) => ({ toolsId, name }));
  }, [data, inventoryMeterIds]);

  const [groupDetailsForm] = Form.useForm<GroupDetailsForm>();

  const handleDeleteMeter = async (meterId: number) => {
    if (!group) return;

    const updatedMeterIds = group.inventory_meters?.filter(meter => meter.id !== meterId).map(meter => meter.id) || [];

    updateMeterGroup({
      id: parsedId,
      data: {
        inventory_meters: updatedMeterIds.filter(
          (updatedMeterId): updatedMeterId is number => updatedMeterId !== undefined,
        ),
      },
    });
  };

  const handleSaveGroupDetails = async (values: GroupDetailsForm) => {
    if (!group) return;

    updateMeterGroup({
      id: parsedId,
      data: {
        name: values.name,
        description: values.description,
        technology: values.technology,
      },
    });
  };

  return (
    <Layout
      footerContent={<GroupDetailFooter />}
      headerContent={intl.formatMessage(messages.header, { name: group?.name })}
    >
      <Space className="w-100" direction="vertical">
        <GroupDetailCard
          group={group}
          groupForm={groupDetailsForm}
          groupId={parsedId}
          isLoading={isLoading}
          title={intl.formatMessage(messages.detailTitle)}
          onFinish={handleSaveGroupDetails}
        />

        <Collapse defaultActiveKey="meters">
          <Collapse.Panel key="meters" header={intl.formatMessage(messages.metersPanel)}>
            <GroupMetersTable isLoading={isLoading} meters={meters || []} onDeleteMeter={handleDeleteMeter} />
          </Collapse.Panel>
        </Collapse>

        {group && (
          <AddMeterForm
            group={group}
            isLoading={isLoading && isUpdatingMeterGroup}
            onAddMeter={async (meterIds: number[]) => {
              updateMeterGroup({
                id: parsedId,
                data: { inventory_meters: meterIds },
              });
            }}
          />
        )}
      </Space>
    </Layout>
  );
};
